import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Chip, FormControl, InputLabel, Select, MenuItem, Alert, FormControlLabel, Checkbox,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { Meeting } from '../services/meetingService';
import PatientActionDialogs, { PatientAction } from '../components/internalTasks/PatientActionDialogs';
import { useTenant } from '../context/TenantContext';
import {
  internalTaskService, InternalTask, INTERNAL_TASK_STATUSES, INTERNAL_TASK_OPEN_STATUSES, INTERNAL_TASK_PRIORITIES,
} from '../services/internalTaskService';
import { internalTaskTypeService, InternalTaskType } from '../services/internalTaskTypeService';
import { formatDateTime } from '../utils/dateUtils';
import { internalTaskColumns } from '../config/internalTaskColumns';
import useTablePage from '../components/table/hooks/useTablePage';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import TablePageLayout from '../components/table/TablePageLayout';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import InternalTaskModal from '../components/internalTasks/InternalTaskModal';

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  Urgent: 'error', High: 'warning', Medium: 'info', Low: 'default',
};

const InternalTasksPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();

  // Users without all_patients only ever receive their own assigned tasks from
  // the server, so the "Only Mine" / assignee filters are redundant and hidden.
  const hasAllPatients = !!user?.user_privilege?.all_patients;

  // Patient-context action modals (opened from the task action button), layered
  // on top of the task modal without navigating away.
  const [actionPatientId, setActionPatientId] = useState<number | null>(null);
  const [patientAction, setPatientAction] = useState<PatientAction>(null);
  const [summaryMeeting, setSummaryMeeting] = useState<Meeting | null>(null);
  const closePatientActions = () => { setActionPatientId(null); setPatientAction(null); setSummaryMeeting(null); };

  const [onlyMine, setOnlyMine] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('Open');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<number | ''>('');
  const [taskTypes, setTaskTypes] = useState<InternalTaskType[]>([]);

  const [editTask, setEditTask] = useState<InternalTask | null>(null);

  const getSortValue = useCallback((t: InternalTask, key: string): any => {
    switch (key) {
      case 'type': return (t.task_type_name || '').toLowerCase();
      case 'priority': return INTERNAL_TASK_PRIORITIES.indexOf(t.priority);
      case 'status': return t.status;
      case 'patient': return (t.patient_name || '').toLowerCase();
      case 'description': return (t.description || '').toLowerCase();
      case 'createdBy': return (t.created_by_name || '').toLowerCase();
      case 'created': return t.created_at;
      case 'updated': return t.updated_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((t: InternalTask): string[] => [
    t.task_type_name || '', t.patient_name || '', t.patient_hm_id || '',
    t.description || '', t.status, t.priority, ...(t.assignee_names || []),
  ], []);

  const table = useTablePage<InternalTask>({
    entityType: 'internalTasks',
    columns: internalTaskColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const reload = useCallback(() => {
    if (!currentTenant?.id) return;
    table.loadData(() => internalTaskService.getInternalTasks());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (currentTenant?.id) internalTaskTypeService.getAll(currentTenant.id).then(setTaskTypes).catch(() => {});
  }, [currentTenant?.id]);

  const filteredData = useMemo(() => {
    let rows = table.sortedData;
    if (statusFilter === 'Open') rows = rows.filter(t => INTERNAL_TASK_OPEN_STATUSES.includes(t.status));
    else if (statusFilter) rows = rows.filter(t => t.status === statusFilter);
    if (priorityFilter) rows = rows.filter(t => t.priority === priorityFilter);
    if (typeFilter) rows = rows.filter(t => t.task_type_id === typeFilter);
    if (onlyMine && user) rows = rows.filter(t => (t.assign_to || []).includes(user.id) || t.created_by === user.id);
    return rows;
  }, [table.sortedData, statusFilter, priorityFilter, typeFilter, onlyMine, user]);

  const getColumnValue = (t: InternalTask, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'type': return t.task_type_name || '-';
      case 'priority': return <Chip label={t.priority} color={PRIORITY_COLORS[t.priority] || 'default'} size="small" />;
      case 'status': return t.status;
      case 'patient': return t.patient_name || '-';
      case 'description': return t.description || '-';
      case 'assignees': return (t.assignee_names || []).join(', ') || '-';
      case 'createdBy': return t.created_by_name || '-';
      case 'created': return formatDateTime(t.created_at);
      case 'updated': return formatDateTime(t.updated_at);
      default: return '';
    }
  };

  return (
    <TablePageLayout loading={false} error={null}>
      <DataTableToolbar
        {...table.toolbarProps}
        title="Internal Tasks"
        searchPlaceholder="Search tasks..."
        isAdmin={!!user?.user_privilege?.config}
        actions={
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            {hasAllPatients && (
              <FormControlLabel
                control={<Checkbox checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} size="small" />}
                label="Only Mine"
              />
            )}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="">All</MenuItem>
                {INTERNAL_TASK_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Priority</InputLabel>
              <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {INTERNAL_TASK_PRIORITIES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value as number | '')}>
                <MenuItem value="">All</MenuItem>
                {taskTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        }
      />

      {table.error && <Alert severity="error" sx={{ mb: 2 }}>{table.error}</Alert>}

      {table.loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={filteredData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            getRowKey={(t) => t.id}
            onRowClick={(_e, t) => setEditTask(t)}
          />
        </DataTableContainer>
      )}

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!user?.user_privilege?.config} />

      {editTask && (
        <InternalTaskModal
          open
          onClose={() => setEditTask(null)}
          onSaved={reload}
          patientId={editTask.patient_id}
          task={editTask}
          onOpenEditPatient={() => { setActionPatientId(editTask.patient_id); setPatientAction('edit_patient'); }}
          onOpenNewMeeting={() => { setActionPatientId(editTask.patient_id); setPatientAction('set_meeting'); }}
          onOpenSummary={(m) => { setActionPatientId(editTask.patient_id); setSummaryMeeting(m); }}
        />
      )}

      {actionPatientId && (
        <PatientActionDialogs
          patientId={actionPatientId}
          action={patientAction}
          summaryMeeting={summaryMeeting}
          onClose={closePatientActions}
          onSaved={reload}
        />
      )}
    </TablePageLayout>
  );
};

export default InternalTasksPage;
